import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, SearchableSelect, Spinner } from '../atoms';
import { useEntityPermission } from '../lib/usePermissions';
import { groupsApi, asGroupArray, GROUPS_QK, Group } from '../lib/groups';
import { GroupCustomFields } from './GroupCustomFields';

/**
 * EntityGroupsPanel — the shared "Groups" panel shown on every internal record's
 * detail page (its Groups tab). Lists the groups this record is in, lets a staff
 * user add it to a group (an existing one, or a new one created inline), and
 * remove it. The record IS what's in the group — there is no separate member
 * object; adding/removing is just the object↔group link.
 *
 * Gated on the `group` permission: renders only with can_view; the add/remove
 * controls appear only with can_edit (agents view, office admins/analysts edit).
 * Internal only — never rendered in the client/consultant portal (spec FR-7).
 *
 * `entityType` is the SINGULAR canonical type (parcel/client/…), matching the
 * /api/group-items join. Pass it from a detail page via groupableTypeForTable().
 */

export interface EntityGroupsPanelProps {
  entityType: string;
  entityId: string | undefined;
}

export function EntityGroupsPanel({ entityType, entityId }: EntityGroupsPanelProps) {
  const qc = useQueryClient();
  const canView = useEntityPermission('group', 'can_view');
  const canEdit = useEntityPermission('group', 'can_edit');
  const [pick, setPick] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const inGroupsQ = useQuery({
    queryKey: entityId ? GROUPS_QK.forEntity(entityType, entityId) : ['group-items', 'entity', 'none'],
    queryFn: ({ signal }) => groupsApi.forEntity(entityType, entityId as string, signal),
    enabled: !!entityId && canView,
  });
  const allGroupsQ = useQuery({
    queryKey: GROUPS_QK.all,
    queryFn: ({ signal }) => groupsApi.list(signal),
    enabled: canView && canEdit,
    staleTime: 30_000,
  });

  const current: Group[] = inGroupsQ.data?.groups ?? [];
  const currentIds = useMemo(() => new Set(current.map((g) => g.id)), [current]);
  const available = useMemo(
    () => asGroupArray(allGroupsQ.data).filter((g) => !currentIds.has(g.id)),
    [allGroupsQ.data, currentIds],
  );

  const invalidate = () => {
    if (entityId) qc.invalidateQueries({ queryKey: GROUPS_QK.forEntity(entityType, entityId) });
    qc.invalidateQueries({ queryKey: GROUPS_QK.all });
  };

  const addMut = useMutation({
    mutationFn: (groupId: string) => groupsApi.add(groupId, entityType, entityId as string),
    onSuccess: () => { setPick(null); setErr(null); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not add to group'),
  });
  const removeMut = useMutation({
    mutationFn: (groupId: string) => groupsApi.removeItem(groupId, entityType, entityId as string),
    onSuccess: () => { setErr(null); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not remove from group'),
  });
  const createMut = useMutation({
    mutationFn: async (name: string) => {
      const g = await groupsApi.create({ name });
      await groupsApi.add(g.id, entityType, entityId as string);
      return g;
    },
    onSuccess: () => { setCreating(false); setNewName(''); setErr(null); invalidate(); },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Could not create group'),
  });

  if (!canView) return null;

  const busy = addMut.isPending || removeMut.isPending || createMut.isPending;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-md, 15px)', color: 'var(--color-ink)' }}>
          Groups{!inGroupsQ.isLoading && current.length > 0 ? ` (${current.length})` : ''}
        </h3>
        <p style={{ margin: '2px 0 0', fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>
          The groups this record belongs to. Adding it to a group just links it — it appears here and in that group. A group's fields below belong to the group and are shared by every record in it.
        </p>
      </div>

      {inGroupsQ.isLoading ? (
        <Spinner size="sm" />
      ) : current.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)' }}>Not in any group yet.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {current.map((g) => (
            <div
              key={g.id}
              style={{
                border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                background: 'var(--color-card)', padding: 'var(--sp-3)',
                display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
                <Link to={`/groups/${g.id}`} style={{ color: 'var(--brand-primary, var(--color-ink))', textDecoration: 'none', fontWeight: 600, fontSize: 'var(--fs-md, 15px)' }}>
                  {g.name}
                </Link>
                {canEdit && (
                  <button
                    type="button"
                    aria-label={`Remove from ${g.name}`}
                    title={`Remove from ${g.name}`}
                    onClick={() => removeMut.mutate(g.id)}
                    disabled={busy}
                    style={{
                      border: 0, background: 'transparent', cursor: 'pointer', lineHeight: 1,
                      color: 'var(--color-ink-3)', fontSize: '18px', padding: '0 2px',
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
              <GroupCustomFields groupId={g.id} canEdit={canEdit} compact />
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', maxWidth: 420 }}>
          {!creating ? (
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <SearchableSelect
                  options={available.map((g) => ({ value: g.id, label: g.name }))}
                  value={pick}
                  onChange={setPick}
                  placeholder={available.length ? 'Add to a group…' : 'No other groups yet'}
                  aria-label="Add to a group"
                />
              </div>
              <Button size="sm" variant="primary" disabled={!pick || busy} onClick={() => pick && addMut.mutate(pick)}>
                Add
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setCreating(true); setErr(null); }}>
                + New group
              </Button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) createMut.mutate(newName.trim()); if (e.key === 'Escape') setCreating(false); }}
                placeholder="New group name"
                aria-label="New group name"
                style={{
                  flex: 1, minWidth: 200, padding: '8px 10px', fontSize: 'var(--fs-sm)',
                  border: '1px solid var(--color-border)', borderRadius: 'var(--r-md, 8px)',
                  background: 'var(--color-card)', color: 'var(--color-ink)',
                }}
              />
              <Button size="sm" variant="primary" disabled={!newName.trim() || busy} onClick={() => createMut.mutate(newName.trim())}>
                Create &amp; add
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => { setCreating(false); setNewName(''); }}>
                Cancel
              </Button>
            </div>
          )}
          {err && <p style={{ margin: 0, fontSize: 'var(--fs-sm)', color: 'var(--color-status-red, #b5432e)' }}>{err}</p>}
        </div>
      )}
    </div>
  );
}
